import React, { useState, useEffect, useRef } from 'react';
import styles from './EditProfileModal.module.css';
import axiosInstance from '../../axiosInstance';
import { toast } from 'react-hot-toast';
import { useAuth } from 'src/hooks/useAuth';
import { Avatar, CircularProgress, Select, MenuItem, TextField } from '@mui/material';
import LiveSelect from 'react-select';
import { useApiKeys } from 'src/hooks/useApiKeys';
import { useCategoriesApi } from 'src/context/CategoriesApi';
import CategoriesModal from '../Onboarding/CategoriesModal';
import bus from 'src/bus';
import { useSearchParams } from 'react-router-dom';

const EditProfileModal = ({ isOpen, onClose, onProfileUpdate }) => {
  const { dispatch, data } = useAuth();
  const apiKeys = useApiKeys();
  const categories = useCategoriesApi();
  const bankSectionRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [profileInfo, setProfileInfo] = useState({ ...data });
  const [isPending, setIsPending] = useState(false);
  
  // Logo state
  const [logoPreview, setLogoPreview] = useState(null);
  const [url, setUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Location state (now hardcoded)
  const hardcodedCountries = [
    { name: 'Nigeria', states: ['Lagos', 'Abuja', 'Kano', 'Rivers'] },
    { name: 'Kenya', states: ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'] },
    { name: 'Egypt', states: ['Cairo', 'Giza', 'Alexandria', 'Aswan'] },
    { name: 'Brazil', states: ['São Paulo', 'Rio de Janeiro', 'Bahia', 'Paraná'] },
    { name: 'Argentina', states: ['Buenos Aires', 'Córdoba', 'Mendoza', 'Santa Fe'] },
    { name: 'Colombia', states: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla'] },
    { name: 'United Kingdom', states: ['England', 'Scotland', 'Wales', 'Northern Ireland'] },
    { name: 'Germany', states: ['Bavaria', 'Berlin', 'Hamburg', 'Hesse'] },
    { name: 'France', states: ['Île-de-France', 'Provence', 'Normandy', 'Brittany'] },
  ];
  
  // Bank state
  const [banks, setBanks] = useState([]);
  const [bank_info, setBankInfo] = useState({ bank_name: '', bank_code: '', account_no: '', account_name: '' });
  const [selectedBank, setSelectedBank] = useState(null);
  const [nameEnquiryLoading, setNameEnquiryLoading] = useState(false);

  // Category state
  const [showCatModal, setShowCatModal] = useState(false);
  const [catData, setCatData] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');

  // Hardcoded Nigerian store categories
  const nigerianStoreCategories = [
    'Supermarket',
    'Pharmacy',
    'Electronics Store',
    'Fashion Boutique',
    'Restaurant',
    'Fast Food',
    'Bakery',
    'Salon & Spa',
    'Barber Shop',
    'Petrol Station',
    'Hardware Store',
    'Bookstore',
    'Stationery',
    'Mobile Phone Store',
    'Computer Store',
    'Jewelry Store',
    'Shoe Store',
    'Beauty Supply',
    'Grocery Store',
    'Mini Mart',
    'Furniture Store',
    'Clothing Store',
    'Cosmetics Store',
    'Sporting Goods',
    'Toy Store',
    'Music Store',
    'Art Gallery',
    'Gift Shop',
    'Laundry Service',
    'Dry Cleaning',
    'Internet Cafe',
    'Printing Services',
    'Photography Studio',
    'Travel Agency',
    'Insurance Agency',
    'Banking Services',
    'Real Estate',
    'Construction',
    'Transportation',
    'Hospitality',
    'Education',
    'Healthcare',
    'Automotive',
    'Agriculture',
    'Manufacturing',
    'Other'
  ];

  const handleBankAccountChange = (key, value) => {
    setBankInfo(prev => ({...prev, [key]: value}));
  }

  // Populate form with initial data
  useEffect(() => {
    if (data) {
      setProfileInfo({ ...data });

      const dataType = data.merchant_type === 'shop' ? data.products_sold : data.product_categories;
      setCatData(dataType?.map((el) => el?.category_name) || []);
      
      const logo = data.merchant_type === 'brand' ? (data.business_logo || data.business_image) : data.store_logo;
      setLogoPreview(logo);

      const acc_name = data.merchant_type === 'brand' ? data.bank_account_name : data.store_account_name;
      const acc_no = data.merchant_type === 'brand' ? data.bank_account_number : data.store_account_number;
      const bankName = data.merchant_type === 'brand' ? data.bank_name : data.store_bank_name;

      setBankInfo(prev => ({...prev, account_name: acc_name, account_no: acc_no, bank_name: bankName}));

      if(banks.length > 0 && bankName){
        const currentBank = banks.find(b => b.name === bankName);
        if(currentBank) {
            setSelectedBank({label: currentBank.name, value: currentBank.code});
            setBankInfo(prev => ({...prev, bank_code: currentBank.code}));
        }
      }
    }
  }, [data, banks]);

  // Fetch banks
  useEffect(() => {
    const fetchBanks = async () => {
        try {
            const banksApi = await fetch('https://api.paystack.co/bank');
            const banksJson = await banksApi.json();
            if (banksJson.status) {
                setBanks(banksJson.data);
            }
        } catch(e) {
            console.error("Could not fetch banks", e);
        }
    };
    fetchBanks();
  }, []);

  // Name enquiry
  useEffect(() => {
    const nameEnquiry = async () => {
      // Guard clause to prevent unnecessary calls
      if (!bank_info.account_no || bank_info.account_no.length < 10 || !bank_info.bank_code) {
        // Clear account name if checks fail
        if (bank_info.account_name) {
            handleBankAccountChange('account_name', '');
        }
        return;
      }

      setNameEnquiryLoading(true);
      try {
        const accountNameApi = await fetch(
          `https://api.paystack.co/bank/resolve?account_number=${bank_info.account_no}&bank_code=${bank_info.bank_code}`,
          {
            headers: {
              Authorization: `Bearer ${apiKeys.PASTACK.SECRET_KEY}`,
            },
          }
        );
        const accountName = await accountNameApi.json();
        if (accountName.status) {
          handleBankAccountChange('account_name', accountName.data.account_name);
        } else {
          handleBankAccountChange('account_name', '');
          toast.error(accountName.message);
        }
      } catch (error) {
        toast.error("Couldn't resolve account name");
      } finally {
        setNameEnquiryLoading(false);
      }
    };

    if(apiKeys.PASTACK?.SECRET_KEY) {
        nameEnquiry();
    }
  }, [bank_info.account_no, bank_info.bank_code]); // Removed apiKeys from dependency array

  // Fetch categories when modal opens if categories are empty
  useEffect(() => {
    if (isOpen && categories.length === 0) {
      setIsPending(true);
      // Trigger the categories fetch via the existing context
      bus.emit('fetchProductCategory');
      setIsPending(false);
    }
  }, [isOpen, categories.length]);

  useEffect(() => {
    if (profileInfo) {
      setProfileInfo(profileInfo);
    }
  }, [profileInfo]);

  useEffect(() => {
    if (isOpen && searchParams.get('bank') === '1' && bankSectionRef.current) {
      setTimeout(() => {
        bankSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isOpen, searchParams]);

  if (!isOpen) return null;

  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setProfileInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleBankChange = (selectedOption) => {
    setSelectedBank(selectedOption);
    setBankInfo(prev => ({...prev, bank_code: selectedOption.value, bank_name: selectedOption.label}));
  }

  const getUploadedFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUrl(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };
  
  const passProducts = (value) => {
    setCatData(value);
  };

  // Filter categories based on search
  const filteredCategories = nigerianStoreCategories.filter(cat =>
    cat.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsPending(true);

    const payload = { ...profileInfo };
    const isBrand = data.merchant_type === 'brand';
    const isShop = data.merchant_type === 'shop';

    if (isShop) {
      payload.store_bank_name = bank_info.bank_name || '';
      payload.store_account_name = bank_info.account_name || '';
      payload.store_account_number = bank_info.account_no || '';
    }
    if (isBrand) {
      payload.bank_name = bank_info.bank_name || '';
      payload.bank_account_name = bank_info.account_name || '';
      payload.bank_account_number = bank_info.account_no || '';
    }
    delete payload?.products_sold;
    delete payload?.product_categories;
    if (payload.team_members) {
      delete payload.team_members;
    }
    
    let catId = categories.filter((category) => catData.includes(category.category_name)).map((category) => category._id);

    const formData = new FormData();
    for (let i = 0; i < catId.length; i++) {
        if (isShop) formData.append('products_sold', catId[i]);
        else formData.append('product_categories', catId[i]);
    }

    Object.keys(payload).forEach((key) => formData.append(key, payload[key]));
    if (url) {
        if (isShop) {
            if (formData.has('store_logo')) formData.delete('store_logo');
            formData.append('store_logo', url);
        } else {
            if (formData.has('business_logo')) formData.delete('business_logo');
            formData.append('business_logo', url);
        }
    }

    try {
      const response = await axiosInstance.put('/merchant/edit-profile', formData);
      toast.success(response.data.message || 'Profile updated successfully!');
      
      if(response.data.data){
        // Merge old and new data to preserve all properties
        const updatedAuthData = { ...data, ...response.data.data, token: data.token };
        dispatch({ type: 'LOGIN', payload: updatedAuthData });
        localStorage.setItem('auth', JSON.stringify(updatedAuthData));
      }
      onProfileUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile.');
    } finally {
      setIsPending(false);
    }
  };

  const isShop = data.merchant_type === 'shop';
  const isBrand = data.merchant_type === 'brand';

  return (
    <>
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Profile</h2>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          <div className={styles.logo_upload_section}>
            <Avatar src={logoPreview} sx={{ width: 80, height: 80 }} />
            <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileInputRef} onChange={getUploadedFile} />
            <button type="button" className={styles.upload_button} onClick={() => fileInputRef.current.click()}> Change Logo </button>
          </div>

          {isBrand && (
            <>
              <div className={styles.form_group}> <label>Business Name</label> <TextField name="business_name" value={profileInfo.business_name} onChange={handleValueChange} fullWidth size="small" /> </div>
              <div className={styles.form_group}> <label>Business Address</label> <TextField name="business_address" value={profileInfo.business_address} onChange={handleValueChange} fullWidth size="small" /> </div>
            </>
          )}

          {isShop && (
             <>
                <div className={styles.form_group}> <label>Store Name</label> <TextField name="store_name" value={profileInfo.store_name} onChange={handleValueChange} fullWidth size="small" /> </div>
                <div className={styles.form_group}> <label>Store Address</label> <TextField name="store_address" value={profileInfo.store_address} onChange={handleValueChange} fullWidth size="small" /> </div>
                <div className={styles.form_group}>
                    <label>Onboarded by</label>
                    <TextField value={profileInfo.onboarder_id || ''} fullWidth size="small" InputProps={{ readOnly: true }}/>
                </div>
                <div className={styles.form_group}>
                  <label>Monthly Income</label>
                  <Select
                    name="monthly_income"
                    value={profileInfo.monthly_income || ''}
                    onChange={handleValueChange}
                    fullWidth
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="NGN 10,000 - NGN 50,000">NGN 10,000 - NGN 50,000</MenuItem>
                    <MenuItem value="NGN 50,000 - NGN 100,000">NGN 50,000 - NGN 100,000</MenuItem>
                    <MenuItem value="NGN 100,000 - NGN 250,000">NGN 100,000 - NGN 250,000</MenuItem>
                    <MenuItem value="NGN 250,000 - NGN 500,000">NGN 250,000 - NGN 500,000</MenuItem>
                    <MenuItem value="NGN 500,000 - above">NGN 500,000 - above</MenuItem>
                  </Select>
                </div>
             </>
          )}
          
          <div className={styles.form_group}>
            <label>Store Category</label>
            <Select
              value={profileInfo.store_category || ''}
              onChange={e => setProfileInfo(prev => ({ ...prev, store_category: e.target.value }))}
              fullWidth
              size="small"
              displayEmpty
              onOpen={() => setCategorySearch('')}
            >
              <MenuItem value="">Select category</MenuItem>
              <MenuItem disabled>
                <TextField
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  size="small"
                  fullWidth
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                />
              </MenuItem>
              {filteredCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </div>
          
          <div className={styles.form_group_row}>
            <div className={styles.form_group}>
              <label>Country</label>
              <Select
                value={profileInfo.country || ''}
                onChange={e => setProfileInfo(prev => ({ ...prev, country: e.target.value, state: '' }))}
                fullWidth
                size="small"
              >
                <MenuItem value="">Select Country</MenuItem>
                {hardcodedCountries.map((c) => ( <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem> ))}
              </Select>
            </div>
            <div className={styles.form_group}>
              <label>State</label>
              <Select
                value={profileInfo.state || ''}
                onChange={e => setProfileInfo(prev => ({ ...prev, state: e.target.value }))}
                fullWidth
                size="small"
                disabled={!profileInfo.country}
              >
                <MenuItem value="">Select State</MenuItem>
                {(hardcodedCountries.find(c => c.name === profileInfo.country)?.states || []).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </div>
          </div>
          
          <div className={styles.form_group} ref={bankSectionRef}>
            <label>Bank</label>
            <LiveSelect
                value={selectedBank}
                name="bank_name"
                onChange={handleBankChange}
                options={banks.map((option) => ({ value: option.code, label: option.name }))}
            />
          </div>

          <div className={styles.form_group}>
            <label>Account Number</label>
            <TextField
                required
                type="number"
                size="small"
                fullWidth
                value={bank_info.account_no}
                name="account_no"
                disabled={!bank_info.bank_code}
                onChange={e => handleBankAccountChange('account_no', e.target.value)}
            />
             {nameEnquiryLoading && (
                <CircularProgress style={{ color: '#af3585', marginLeft: 8 }} size="1rem" />
            )}
          </div>
            
          <div className={styles.form_group}>
            <label>Account Name</label>
            <TextField
                value={bank_info.account_name || ''}
                name="account_name"
                fullWidth
                size="small"
                placeholder="Account name will appear here"
                InputProps={{ readOnly: true }}
            />
          </div>

          <button type="submit" disabled={isPending} className={styles.submitButton} style={{ width: '100%', background: '#af3585', color: '#fff', border: 'none', borderRadius: 8, padding: '14px 0', fontWeight: 600, fontSize: 16, marginTop: 12,marginBottom: 35 }}>
            {isPending ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
    <CategoriesModal
        show={showCatModal}
        handleClose={() => setShowCatModal(false)}
        passProducts={passProducts}
        selected={catData}
      />
    </>
  );
};

export default EditProfileModal; 